import { Viewer } from '../viewer/viewer';
import { buildClipState, filterClipStateByMask, mergeClipFragment } from '../viewer/buildClipState';
import { applyOverrides } from '../viewer/MaterialOverrideStack';
import { resolveSettings } from '../viewer/RenderViewSettings';
import * as THREE from 'three';
import type { PointCloudOctree } from '../PointCloudOctree';

/**
 * Apply the main RenderView's per-view overrides (clipOverride +
 * materialOverride + resolved settings) to `pointclouds` for an off-screen
 * capture; returns the restore function.
 *
 * Mirrors viewer.renderOneView's override stack. Without it, tools that
 * install a temporary clip box on the main view (e.g. DoorWindowTool wall
 * focus) don't see their clip take effect in a capture, because the shared
 * material only carries the base scene clip state after viewer.update()
 * restores it. The main view's floor-isolation fragment is baked into the
 * clip too (it lives off-scene, so `buildClipState` alone wouldn't include it).
 * `hiddenClipIds` leaves scene clip volumes out of the capture (by identifier).
 */
export function applyMainViewOverrides(viewer: Viewer, pointclouds: PointCloudOctree[], hiddenClipIds: readonly string[] = []): () => void {
  const mainView = viewer.renderViews[0];
  if (!mainView) return () => undefined;
  const sceneClipState = buildClipState(viewer);
  const baseClipState = hiddenClipIds.length > 0 ? filterClipStateByMask(sceneClipState, Object.fromEntries(hiddenClipIds.map((id) => [id, false]))) : sceneClipState;
  const resolved = resolveSettings(mainView.settings, baseClipState);
  const mergedMaterial = { ...resolved.materialOverride, ...mainView.materialOverride };
  let effectiveClipOverride = mainView.clipOverride ?? resolved.clipOverride;
  if (mainView.floorIsolation && effectiveClipOverride?.mode !== 'none') {
    const baseState = effectiveClipOverride?.mode === 'replace' ? effectiveClipOverride.state : baseClipState;
    effectiveClipOverride = { mode: 'replace', state: mergeClipFragment(baseState, mainView.floorIsolation) };
  }
  return applyOverrides(pointclouds, mergedMaterial, effectiveClipOverride, baseClipState, viewer.clippingTool.maxPolygonVertices);
}

export class ScreenshotRenderer {
  viewer: Viewer;

  private readonly targetResolution = 3840; // Screenshot resolution target (4x 1920)

  constructor(viewer: Viewer) {
    this.viewer = viewer;
  }

  /**
   * Core rendering method - renders a region at specified resolution
   * Returns raw buffer and metadata without cropping
   */
  private renderRegion(
    viewLeft: number,
    viewRight: number,
    viewBottom: number,
    viewTop: number,
    renderWidth: number,
    renderHeight: number,
    renderObjects: boolean,
    positionEncoding?: {
      eyeMin: [number, number, number];
      eyeMax: [number, number, number];
    },
  ): { buffer: Uint8Array; camera: THREE.OrthographicCamera } {
    const viewer = this.viewer;
    const camera = viewer.scene.getActiveCamera();

    // Create custom orthographic camera for the region
    const customCamera = new THREE.OrthographicCamera(viewLeft, viewRight, viewTop, viewBottom, camera.near, camera.far);
    customCamera.position.copy(camera.position);
    customCamera.quaternion.copy(camera.quaternion);
    customCamera.up.copy(camera.up);
    customCamera.scale.copy(camera.scale);
    customCamera.updateMatrix();
    customCamera.updateMatrixWorld(true);
    customCamera.updateProjectionMatrix();

    const renderTarget = new THREE.WebGLRenderTarget(1, 1, {
      minFilter: positionEncoding ? THREE.NearestFilter : THREE.LinearFilter,
      magFilter: THREE.NearestFilter,
      format: THREE.RGBAFormat,
    });
    renderTarget.setSize(renderWidth, renderHeight);
    this.viewer.renderer.setRenderTarget(renderTarget);

    const visiblePointClouds = viewer.scene.pointclouds.filter((pc) => pc.visible);

    // COLOR & DEPTH PASS
    for (const pointcloud of visiblePointClouds) {
      const octreeSize = pointcloud.pcoGeometry.boundingBox.getSize(new THREE.Vector3()).x;
      const material = pointcloud.material;
      material.weighted = false;
      material.useLogarithmicDepthBuffer = false;
      material.useEDL = false;
      material.screenWidth = renderWidth;
      material.screenHeight = renderHeight;
      material.uniforms.visibleNodes.value = pointcloud.material.visibleNodesTexture;
      material.uniforms.octreeSize.value = octreeSize;
      material.spacing = pointcloud.pcoGeometry.spacing;
    }

    // Apply the main RenderView's per-view overrides (clipOverride +
    // materialOverride + resolved settings) for the duration of the capture.
    // Without this, tools that install a temporary clip box on the main view
    // (e.g. DoorWindowTool wall focus) don't see their clip take effect in
    // the screenshot, because the shared material only carries the base scene
    // clip state after viewer.update() restores it.
    //
    // Mirrors viewer.renderOneView's override stack. ORDER MATTERS: applied
    // FIRST so the position-encoding swap below layers on top — otherwise an
    // override `activeAttributeName` (e.g. X-Ray mode setting density) would
    // clobber the `world_position` encoding pass and the position map would
    // come back as whatever the override wanted to render.
    const restoreOverrides = applyMainViewOverrides(viewer, visiblePointClouds);

    // Save and swap material state for position encoding mode. Runs AFTER
    // applyOverrides so the encoding wins over any override field
    // (activeAttributeName, densityMode, useEDL) the merged override would
    // otherwise re-enable on top of the line 64 defaults.
    //
    // useEDL in particular: the fragment shader's `use_edl` branch overwrites
    // alpha with logarithmic depth, but the position encoding packs the low
    // bits of eye-X/Y into alpha. If EDL stays compiled in, the position map
    // comes back as a smooth gradient with no per-point mosaic. The shader
    // also guards this with `#if !defined(color_type_world_position)`, but
    // we set useEDL = false here too so the JS pipeline is self-consistent.
    const savedState: Array<{
      activeAttributeName: string | null;
      useEDL: boolean;
      eyeMin: number[];
      eyeMax: number[];
      densityMode: boolean;
      opacity: number;
    }> = [];
    if (positionEncoding) {
      for (const pointcloud of visiblePointClouds) {
        const material = pointcloud.material;
        savedState.push({
          activeAttributeName: material.activeAttributeName,
          useEDL: material.useEDL,
          eyeMin: [...(material.uniforms.uEyeMin.value as number[])],
          eyeMax: [...(material.uniforms.uEyeMax.value as number[])],
          densityMode: material.densityMode,
          opacity: material.opacity,
        });
        material.densityMode = false; // Disable density mode so position encoding fragment shader runs
        material.opacity = 1.0; // Force opacity=1 to disable GL blending (alpha carries position data, not transparency)
        material.useEDL = false;
        material.activeAttributeName = 'world_position';
        material.uniforms.uEyeMin.value = positionEncoding.eyeMin;
        material.uniforms.uEyeMax.value = positionEncoding.eyeMax;
      }
    }

    const buffer = new Uint8Array(4 * renderWidth * renderHeight);
    try {
      viewer.pRenderer.render(viewer.scene.scenePointCloud, customCamera, null, {});

      if (renderObjects && !positionEncoding) {
        viewer.renderer.setRenderTarget(renderTarget);
        viewer.renderer.render(viewer.volumeScene, customCamera);
        viewer.renderer.clearDepth();
        viewer.renderer.render(viewer.measurementScene, customCamera);
        viewer.renderer.render(viewer.planeScene, customCamera);
      }

      this.viewer.renderer.readRenderTargetPixels(renderTarget, 0, 0, renderWidth, renderHeight, buffer);
    } finally {
      // Restore position-encoding material swaps FIRST so the override
      // restore puts back the user-facing material state on top of clean
      // (non-encoded) shader settings.
      if (positionEncoding) {
        visiblePointClouds.forEach((pointcloud, i) => {
          const material = pointcloud.material;
          const saved = savedState[i];
          material.activeAttributeName = saved.activeAttributeName;
          material.densityMode = saved.densityMode;
          material.opacity = saved.opacity;
          material.useEDL = saved.useEDL;
          material.uniforms.uEyeMin.value = saved.eyeMin;
          material.uniforms.uEyeMax.value = saved.eyeMax;
        });
      }
      restoreOverrides();
    }

    // Flip vertically
    const bytesPerLine = renderWidth * 4;
    for (let i = 0; i < renderHeight / 2; i++) {
      const j = renderHeight - i - 1;
      const lineI = buffer.slice(i * bytesPerLine, i * bytesPerLine + bytesPerLine);
      const lineJ = buffer.slice(j * bytesPerLine, j * bytesPerLine + bytesPerLine);
      buffer.set(lineJ, i * bytesPerLine);
      buffer.set(lineI, j * bytesPerLine);
    }

    renderTarget.dispose();

    return { buffer, camera: customCamera };
  }

  /**
   * Find non-transparent pixel bounds in a buffer
   */
  private findContentBounds(buffer: Uint8Array, width: number, height: number): { minX: number; maxX: number; minY: number; maxY: number } | null {
    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const alpha = buffer[(y * width + x) * 4 + 3];
        if (alpha > 0) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (maxX < minX || maxY < minY) {
      return null; // No non-transparent pixels
    }

    return { minX, maxX, minY, maxY };
  }

  renderScreenshot(origin?: THREE.Vector2, width?: number, height?: number, renderObjects?: boolean, resolutionMultiplier?: number, skipCrop?: boolean) {
    const viewer = this.viewer;
    const camera = viewer.scene.getActiveCamera() as THREE.OrthographicCamera;

    // Calculate the frustum bounds for the selection or full viewport
    let viewLeft: number, viewRight: number, viewBottom: number, viewTop: number;
    let requestedWidth: number, requestedHeight: number;

    if (origin && width && height) {
      const renderAreaWidth = viewer.renderArea.clientWidth;
      const renderAreaHeight = viewer.renderArea.clientHeight;

      const normLeft = origin.x / renderAreaWidth;
      const normRight = (origin.x + width) / renderAreaWidth;
      const normBottom = origin.y / renderAreaHeight;
      const normTop = (origin.y + height) / renderAreaHeight;

      const frustumWidth = camera.right - camera.left;
      const frustumHeight = camera.top - camera.bottom;

      viewLeft = camera.left + normLeft * frustumWidth;
      viewRight = camera.left + normRight * frustumWidth;
      viewBottom = camera.bottom + normBottom * frustumHeight;
      viewTop = camera.bottom + normTop * frustumHeight;

      requestedWidth = width;
      requestedHeight = height;
    } else {
      viewLeft = camera.left;
      viewRight = camera.right;
      viewBottom = camera.bottom;
      viewTop = camera.top;

      requestedWidth = viewer.renderArea.clientWidth;
      requestedHeight = viewer.renderArea.clientHeight;
    }

    const selectionWidth = viewRight - viewLeft;
    const selectionHeight = viewTop - viewBottom;

    // If skipCrop is true, render at exact requested dimensions without cropping
    // This is used by LineDetectionService which needs 1:1 pixel mapping
    if (skipCrop) {
      const renderWidth = Math.round(requestedWidth * (resolutionMultiplier ?? 1));
      const renderHeight = Math.round(requestedHeight * (resolutionMultiplier ?? 1));

      const { buffer, camera: renderCamera } = this.renderRegion(viewLeft, viewRight, viewBottom, viewTop, renderWidth, renderHeight, renderObjects || false);

      return this.buildScreenshotResult(buffer, renderWidth, renderHeight, renderCamera, camera);
    }

    // Apply resolution multiplier to target resolution
    const effectiveTargetResolution = this.targetResolution * (resolutionMultiplier ?? 1);

    // Two-pass approach: low-res to find bounds, high-res for final render
    const lowResSize = 512;
    const aspectRatio = selectionWidth / selectionHeight;
    const lowResWidth = aspectRatio > 1 ? lowResSize : Math.round(lowResSize * aspectRatio);
    const lowResHeight = aspectRatio > 1 ? Math.round(lowResSize / aspectRatio) : lowResSize;

    // Pass 1: Low-res render to find content bounds
    const { buffer: lowResBuffer } = this.renderRegion(viewLeft, viewRight, viewBottom, viewTop, lowResWidth, lowResHeight, renderObjects || false);

    const bounds = this.findContentBounds(lowResBuffer, lowResWidth, lowResHeight);

    if (!bounds) {
      // No content found, return empty result at low-res
      const { buffer, camera: renderCamera } = this.renderRegion(viewLeft, viewRight, viewBottom, viewTop, lowResWidth, lowResHeight, renderObjects || false);
      return this.buildScreenshotResult(buffer, lowResWidth, lowResHeight, renderCamera, camera);
    }

    // Convert pixel bounds to frustum coordinates
    const worldPerPixelX = selectionWidth / lowResWidth;
    const worldPerPixelY = selectionHeight / lowResHeight;

    // bounds.minY is from top of image, viewTop is camera top
    const croppedViewLeft = viewLeft + bounds.minX * worldPerPixelX;
    const croppedViewRight = viewLeft + (bounds.maxX + 1) * worldPerPixelX;
    const croppedViewTop = viewTop - bounds.minY * worldPerPixelY;
    const croppedViewBottom = viewTop - (bounds.maxY + 1) * worldPerPixelY;

    const croppedWidth = croppedViewRight - croppedViewLeft;
    const croppedHeight = croppedViewTop - croppedViewBottom;
    const croppedAspect = croppedWidth / croppedHeight;

    // Calculate high-res dimensions for the cropped region
    let highResWidth: number, highResHeight: number;
    if (croppedAspect > 1) {
      highResWidth = effectiveTargetResolution;
      highResHeight = Math.round(effectiveTargetResolution / croppedAspect);
    } else {
      highResHeight = effectiveTargetResolution;
      highResWidth = Math.round(effectiveTargetResolution * croppedAspect);
    }

    // Pass 2: High-res render of just the content region
    const { buffer: highResBuffer, camera: renderCamera } = this.renderRegion(croppedViewLeft, croppedViewRight, croppedViewBottom, croppedViewTop, highResWidth, highResHeight, renderObjects || false);

    return this.buildScreenshotResult(highResBuffer, highResWidth, highResHeight, renderCamera, camera);
  }

  /**
   * Build the final screenshot result with georeference data
   */
  private buildScreenshotResult(buffer: Uint8Array, pixelWidth: number, pixelHeight: number, renderCamera: THREE.OrthographicCamera, mainCamera: THREE.OrthographicCamera) {
    const rotation = mainCamera.rotation.z;

    const frustumWorldWidth = renderCamera.right - renderCamera.left;
    const frustumWorldHeight = renderCamera.top - renderCamera.bottom;

    const worldPerPixelX = frustumWorldWidth / pixelWidth;
    const worldPerPixelY = frustumWorldHeight / pixelHeight;

    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);

    const pixelToWorldX = new THREE.Vector2(worldPerPixelX * cos, worldPerPixelX * sin);

    const pixelToWorldY = new THREE.Vector2(worldPerPixelY * sin, -worldPerPixelY * cos);

    const topLeftCameraSpace = new THREE.Vector2(renderCamera.left, renderCamera.top);
    const topLeftWorldOffset = new THREE.Vector2(topLeftCameraSpace.x * cos - topLeftCameraSpace.y * sin, topLeftCameraSpace.x * sin + topLeftCameraSpace.y * cos);
    const screenshotTopLeftWorldPosition = new THREE.Vector2(mainCamera.position.x + topLeftWorldOffset.x, mainCamera.position.y + topLeftWorldOffset.y);

    return {
      buffer,
      pixelWidth,
      pixelHeight,
      position: screenshotTopLeftWorldPosition,
      worldWidth: frustumWorldWidth,
      worldHeight: frustumWorldHeight,
      pixelToWorldX,
      pixelToWorldY,
    };
  }

  /**
   * Render a position map: each pixel encodes the EYE-SPACE (camera-relative)
   * XYZ of the point that landed there, packed at 12+12+8 bits across RGBA.
   * The reader recovers world position via the active camera's inverse view
   * matrix (returned alongside as `inverseViewMatrix`), so the encoding is
   * correct for any camera orientation — top-down floor capture, wall-
   * perpendicular capture for door/window editing, etc.
   *
   * `zRange` is interpreted as a positive eye-space depth range (distance
   * from camera). When omitted we use the camera's near/far.
   *
   * `worldBoxCorners` overrides the eye-Z slab derivation entirely:
   * pass 8 world-space corners of an oriented bounding box (e.g. the
   * wall-focus clip box) and the encoder projects THOSE through the
   * view matrix to derive a tight eye-Z range along the actual capture
   * axis. Needed when the camera looks down a non-Z axis (door/window
   * wall view), where a `[worldMinZ, worldMaxZ]` slab combined with the
   * cloud's X/Y bounds produces an over-wide range.
   */
  renderPositionMap(
    origin?: THREE.Vector2,
    width?: number,
    height?: number,
    resolutionMultiplier?: number,
    zRange?: [number, number],
    exactDimensions?: { width: number; height: number },
    worldBoxCorners?: THREE.Vector3[],
  ): {
    buffer: Uint8Array;
    pixelWidth: number;
    pixelHeight: number;
    /** Eye-space frustum bounds used by the encoding (X/Y from ortho frustum, Z from near/far). */
    eyeMin: [number, number, number];
    eyeMax: [number, number, number];
    /** Inverse view matrix of the capture camera. Multiply decoded eye XYZ by this to get world position. */
    inverseViewMatrix: THREE.Matrix4;
  } | null {
    const viewer = this.viewer;
    const camera = viewer.scene.getActiveCamera() as THREE.OrthographicCamera;

    // Calculate frustum bounds (identical to renderScreenshot)
    let viewLeft: number, viewRight: number, viewBottom: number, viewTop: number;

    if (origin && width && height) {
      const renderAreaWidth = viewer.renderArea.clientWidth;
      const renderAreaHeight = viewer.renderArea.clientHeight;

      const normLeft = origin.x / renderAreaWidth;
      const normRight = (origin.x + width) / renderAreaWidth;
      const normBottom = origin.y / renderAreaHeight;
      const normTop = (origin.y + height) / renderAreaHeight;

      const frustumWidth = camera.right - camera.left;
      const frustumHeight = camera.top - camera.bottom;

      viewLeft = camera.left + normLeft * frustumWidth;
      viewRight = camera.left + normRight * frustumWidth;
      viewBottom = camera.bottom + normBottom * frustumHeight;
      viewTop = camera.bottom + normTop * frustumHeight;
    } else {
      viewLeft = camera.left;
      viewRight = camera.right;
      viewBottom = camera.bottom;
      viewTop = camera.top;
    }

    const selectionWidth = viewRight - viewLeft;
    const selectionHeight = viewTop - viewBottom;

    // Eye-space depth range. Three paths, in priority order:
    //
    //  1. `worldBoxCorners`: caller supplied 8 oriented-box corners
    //     (e.g. the wall-focus clip box). Project each through the view
    //     matrix to get a tight eye-Z range matching the captured slab.
    //     Used when the capture axis isn't world-Z (door/window wall).
    //  2. `zRange`: caller supplied a world-Z slab (floor isolation).
    //     Build an 8-corner box across the cloud's X/Y bounds at those
    //     Zs and project. Pre-existing room behavior.
    //  3. Neither: project the full cloud bounding box, returns a
    //     tight-enough range matching the visible scene.
    //
    // The encoder needs eye-space depth, not world-Z, since the shader
    // writes `-eye.z`. Going through the view matrix gives us the right
    // axis regardless of camera orientation.
    let eyeNear: number, eyeFar: number;
    {
      const viewMat = new THREE.Matrix4().copy(camera.matrixWorld).invert();
      let corners: THREE.Vector3[];
      if (worldBoxCorners && worldBoxCorners.length === 8) {
        corners = worldBoxCorners.map((c) => c.clone());
      } else {
        let worldMinZ: number, worldMaxZ: number, worldMinX: number, worldMaxX: number, worldMinY: number, worldMaxY: number;
        if (zRange) {
          worldMinZ = Math.min(zRange[0], zRange[1]);
          worldMaxZ = Math.max(zRange[0], zRange[1]);
          const bbox = viewer.getBoundingBox(viewer.scene.pointclouds);
          worldMinX = bbox.min.x;
          worldMaxX = bbox.max.x;
          worldMinY = bbox.min.y;
          worldMaxY = bbox.max.y;
        } else {
          const bbox = viewer.getBoundingBox(viewer.scene.pointclouds);
          worldMinX = bbox.min.x;
          worldMaxX = bbox.max.x;
          worldMinY = bbox.min.y;
          worldMaxY = bbox.max.y;
          worldMinZ = bbox.min.z;
          worldMaxZ = bbox.max.z;
        }
        corners = [
          new THREE.Vector3(worldMinX, worldMinY, worldMinZ),
          new THREE.Vector3(worldMaxX, worldMinY, worldMinZ),
          new THREE.Vector3(worldMinX, worldMaxY, worldMinZ),
          new THREE.Vector3(worldMaxX, worldMaxY, worldMinZ),
          new THREE.Vector3(worldMinX, worldMinY, worldMaxZ),
          new THREE.Vector3(worldMaxX, worldMinY, worldMaxZ),
          new THREE.Vector3(worldMinX, worldMaxY, worldMaxZ),
          new THREE.Vector3(worldMaxX, worldMaxY, worldMaxZ),
        ];
      }
      // The corners exist solely to derive the eye-Z range (uEyeMin.z /
      // uEyeMax.z) for the captured slab. They define the slab being
      // captured, NOT the full cloud — the shader's depth override puts the
      // mid-Z layer nearest the camera so it wins the per-pixel depth test
      // where points stack. A caller needing taller coverage (e.g. full wall
      // height in a room capture) must widen the `zRange` / `worldBoxCorners`
      // it passes in; the renderer honors that slab faithfully rather than
      // second-guessing it against the cloud bounds.
      eyeNear = Infinity;
      eyeFar = -Infinity;
      for (const c of corners) {
        // Eye-Z = -(viewMat * worldPos).z, matching the shader's
        // `eyeZ = -eye.z` convention (positive depth from camera).
        const eyeZ = -c.applyMatrix4(viewMat).z;
        eyeNear = Math.min(eyeNear, eyeZ);
        eyeFar = Math.max(eyeFar, eyeZ);
      }
    }
    const zMargin = (eyeFar - eyeNear) * 0.001;
    eyeNear -= zMargin;
    eyeFar += zMargin;

    const posEncoding = {
      eyeMin: [viewLeft, viewBottom, eyeNear] as [number, number, number],
      eyeMax: [viewRight, viewTop, eyeFar] as [number, number, number],
    };

    // Render at the same dimensions as the density mask for 1:1 pixel correspondence.
    // When exactDimensions is provided, use those directly (matches skipCrop path).
    // Otherwise fall back to targetResolution-based calculation.
    let renderWidth: number, renderHeight: number;
    if (exactDimensions) {
      renderWidth = exactDimensions.width;
      renderHeight = exactDimensions.height;
    } else {
      const effectiveTargetResolution = this.targetResolution * (resolutionMultiplier ?? 1);
      const aspectRatio = selectionWidth / selectionHeight;
      if (aspectRatio > 1) {
        renderWidth = Math.round(effectiveTargetResolution);
        renderHeight = Math.round(effectiveTargetResolution / aspectRatio);
      } else {
        renderHeight = Math.round(effectiveTargetResolution);
        renderWidth = Math.round(effectiveTargetResolution * aspectRatio);
      }
    }

    // Single-pass render with position encoding at full requested region.
    // renderRegion creates a custom ortho camera matching `camera` for the
    // selected region; that camera's view matrix is what the encoding is
    // relative to, so we use it to derive the inverse view matrix returned
    // alongside.
    const { buffer, camera: customCamera } = this.renderRegion(viewLeft, viewRight, viewBottom, viewTop, renderWidth, renderHeight, false, posEncoding);
    customCamera.updateMatrixWorld(true);
    const inverseViewMatrix = new THREE.Matrix4().copy(customCamera.matrixWorld);

    return {
      buffer,
      pixelWidth: renderWidth,
      pixelHeight: renderHeight,
      eyeMin: posEncoding.eyeMin,
      eyeMax: posEncoding.eyeMax,
      inverseViewMatrix,
    };
  }

  async renderScreenshotWithOverlays(origin?: THREE.Vector2, width?: number, height?: number, renderObjects?: boolean, resolutionMultiplier?: number) {
    // First, get the basic screenshot
    const screenshotData = this.renderScreenshot(origin, width, height, renderObjects, resolutionMultiplier);

    // Create a canvas from the screenshot data
    const canvas = document.createElement('canvas');
    canvas.width = screenshotData.pixelWidth;
    canvas.height = screenshotData.pixelHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return screenshotData;

    // Put the screenshot data onto the canvas
    const imageData = new ImageData(new Uint8ClampedArray(screenshotData.buffer), screenshotData.pixelWidth, screenshotData.pixelHeight);
    ctx.putImageData(imageData, 0, 0);

    // Look for the heatmap legend overlay
    const heatmapLegend = document.querySelector('[data-screenshot-overlay="heatmap-legend"]') as HTMLElement;
    if (heatmapLegend && heatmapLegend.offsetParent !== null) {
      // offsetParent is null if element is hidden
      this.drawLegendCanvasOnScreenshot(ctx, heatmapLegend, screenshotData.pixelWidth, screenshotData.pixelHeight);
    }

    // Convert canvas back to image data buffer
    const canvasImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const newBuffer = new Uint8Array(canvasImageData.data);

    return {
      ...screenshotData,
      buffer: newBuffer,
    };
  }

  private drawLegendCanvasOnScreenshot(ctx: CanvasRenderingContext2D, element: HTMLElement, screenshotWidth: number, screenshotHeight: number) {
    // Get the legend canvas that already has everything rendered
    const legendCanvas = element.querySelector('canvas');
    if (!legendCanvas) {
      return;
    }

    // Legend dimensions scale with target resolution
    const fixedCanvasWidth = this.targetResolution * 0.12;
    const fixedCanvasHeight = this.targetResolution * 0.2;
    const padding = 16;
    const fontSize = 16;
    const headerHeight = fontSize + padding * 2;

    // Calculate background dimensions to fit the fixed canvas
    const backgroundWidth = fixedCanvasWidth + padding * 2;
    const backgroundHeight = fixedCanvasHeight + headerHeight + padding;

    // Position at top-right corner with margin
    const margin = 20;
    const relativeX = screenshotWidth - backgroundWidth - margin;
    const relativeY = margin;

    // Draw background
    ctx.save();
    ctx.fillStyle = '#242424'; // Always use dark background for screenshots

    // Draw rounded rectangle background
    const borderRadius = 8;
    this.roundRect(ctx, relativeX, relativeY, backgroundWidth, backgroundHeight, borderRadius);
    ctx.fill();

    // Draw header text
    const headerY = relativeY + padding + fontSize;
    ctx.fillStyle = '#ffffff';
    ctx.font = `600 ${fontSize}px "IBM Plex Sans", Arial, sans-serif`;
    ctx.textAlign = 'left';

    // Extract the translated text from the h3 element
    const headerElement = element.querySelector('h3');
    const headerText = headerElement?.textContent || 'Heatmap Legend'; // fallback to English
    ctx.fillText(headerText, relativeX + padding, headerY);

    // Create fixed-size canvas for the legend
    const fixedCanvas = document.createElement('canvas');
    fixedCanvas.width = fixedCanvasWidth;
    fixedCanvas.height = fixedCanvasHeight;
    const fixedCtx = fixedCanvas.getContext('2d');
    if (!fixedCtx) {
      ctx.restore();
      return;
    }

    // Draw the legend canvas into the fixed-size canvas
    fixedCtx.drawImage(legendCanvas, 0, 0, fixedCanvasWidth, fixedCanvasHeight);

    // Calculate position for the fixed-size canvas (below the header)
    const canvasRelativeX = relativeX + padding;
    const canvasRelativeY = relativeY + headerHeight;

    // Draw the fixed-size canvas onto the screenshot
    ctx.drawImage(fixedCanvas, canvasRelativeX, canvasRelativeY, fixedCanvasWidth, fixedCanvasHeight);

    // Draw border around the canvas
    const canvasRadius = 6; // rounded-md
    ctx.strokeStyle = '#6b7280'; // gray-600 to match the border
    ctx.lineWidth = 1;
    this.roundRect(ctx, canvasRelativeX, canvasRelativeY, fixedCanvasWidth, fixedCanvasHeight, canvasRadius);
    ctx.stroke();

    ctx.restore();
  }

  private roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  }
}
